import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import type { InboxDirection } from "./filters";
import { fetchMergedThreadsPage } from "./merged-threads";
import {
  deleteThread,
  getComposeOptions,
  getInboxThreadView,
  getInboxThreadsPage,
  markThreadRead,
  markThreadUnread,
} from "./server-fns";
import type { InboxThreadListItem, InboxThreadsPageView, InboxThreadView } from "./view-model";

const THREADS_PAGE_SIZE = 25;

export type InboxThreadFilters = {
  domainId?: string;
  aliasId?: string;
  /** Two or more inboxes, merged client-side. Build with `inboxSelectionFilter`. */
  aliasIds?: string[];
  direction: InboxDirection;
  search?: string;
  includeBounce?: boolean;
  includeWarmup?: boolean;
};

export function inboxThreadsQueryKey(filters: InboxThreadFilters) {
  return ["inboxThreads", filters] as const;
}

export function inboxThreadQueryKey(threadId: string) {
  return ["inboxThread", threadId] as const;
}

export function composeOptionsQueryOptions() {
  return queryOptions({
    queryKey: ["composeOptions"],
    queryFn: () => getComposeOptions(),
    staleTime: 60_000,
  });
}

export function inboxThreadQueryOptions(threadId: string) {
  return queryOptions({
    queryKey: inboxThreadQueryKey(threadId),
    queryFn: () => getInboxThreadView({ data: { threadId } }),
    staleTime: 60_000,
  });
}

export function inboxThreadsInfiniteQueryOptions(filters: InboxThreadFilters) {
  return infiniteQueryOptions({
    queryKey: inboxThreadsQueryKey(filters),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const fetchPage = (aliasId: string | undefined, cursor: string | null) =>
        getInboxThreadsPage({
          data: {
            domainId: filters.domainId,
            aliasId,
            direction: filters.direction,
            search: filters.search,
            includeBounce: filters.includeBounce,
            includeWarmup: filters.includeWarmup,
            cursor,
            limit: THREADS_PAGE_SIZE,
          },
        });

      if (filters.aliasIds && filters.aliasIds.length > 1) {
        return fetchMergedThreadsPage({
          aliasIds: filters.aliasIds,
          cursor: pageParam,
          pageSize: THREADS_PAGE_SIZE,
          fetchPage,
        });
      }

      return fetchPage(filters.aliasId, pageParam);
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 15_000,
  });
}

export function flattenInboxThreadPages(data: InfiniteData<InboxThreadsPageView, unknown>) {
  // A merged list can meet the same conversation again on a later page, when it
  // involves two of the selected inboxes. Keep the first (newest) occurrence.
  const seen = new Set<string>();
  return data.pages
    .flatMap((page) => page.items)
    .filter((thread) => (seen.has(thread.id) ? false : (seen.add(thread.id), true)));
}

function updateInboxThreadInPages(
  pages: InfiniteData<InboxThreadsPageView, unknown> | undefined,
  threadId: string,
  updater: (thread: InboxThreadListItem) => InboxThreadListItem,
) {
  if (!pages) return pages;

  return {
    ...pages,
    pages: pages.pages.map((page) => ({
      ...page,
      items: page.items.map((thread) => (thread.id === threadId ? updater(thread) : thread)),
    })),
  };
}

export function removeInboxThreadFromPages(
  pages: InfiniteData<InboxThreadsPageView, unknown> | undefined,
  threadId: string,
) {
  if (!pages) return pages;

  return {
    ...pages,
    pages: pages.pages.map((page) => ({
      ...page,
      items: page.items.filter((thread) => thread.id !== threadId),
    })),
  };
}

function applyThreadReadState(thread: InboxThreadView, unreadCount: number) {
  return {
    ...thread,
    hasUnread: unreadCount > 0,
    unreadCount,
  };
}

export function markThreadReadMutationOptions(queryClient: QueryClient, threadId: string) {
  return {
    mutationFn: () => markThreadRead({ data: { threadId } }),
    onMutate: async () => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: inboxThreadQueryKey(threadId) }),
        queryClient.cancelQueries({ queryKey: ["inboxThreads"] }),
      ]);

      const previousThread = queryClient.getQueryData<InboxThreadView>(
        inboxThreadQueryKey(threadId),
      );
      const previousPages = queryClient.getQueriesData<InfiniteData<InboxThreadsPageView, unknown>>(
        {
          queryKey: ["inboxThreads"],
        },
      );

      queryClient.setQueryData<InboxThreadView>(inboxThreadQueryKey(threadId), (current) => {
        if (!current) return current;
        return applyThreadReadState(current, 0);
      });

      queryClient.setQueriesData<InfiniteData<InboxThreadsPageView, unknown>>(
        { queryKey: ["inboxThreads"] },
        (current) =>
          updateInboxThreadInPages(current, threadId, (thread) => ({
            ...thread,
            hasUnread: false,
            unreadCount: 0,
          })),
      );

      return { previousThread, previousPages };
    },
    onError: (
      _error: unknown,
      _variables: void,
      context?: {
        previousThread?: InboxThreadView;
        previousPages?: Array<
          readonly [unknown, InfiniteData<InboxThreadsPageView, unknown> | undefined]
        >;
      },
    ) => {
      if (context?.previousThread) {
        queryClient.setQueryData(inboxThreadQueryKey(threadId), context.previousThread);
      }

      for (const [queryKey, data] of context?.previousPages || []) {
        queryClient.setQueryData(queryKey as readonly unknown[], data);
      }
    },
    onSettled: async () => {},
  };
}

export function markThreadUnreadMutationOptions(queryClient: QueryClient, threadId: string) {
  return {
    mutationFn: () => markThreadUnread({ data: { threadId } }),
    onMutate: async () => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: inboxThreadQueryKey(threadId) }),
        queryClient.cancelQueries({ queryKey: ["inboxThreads"] }),
      ]);

      const previousThread = queryClient.getQueryData<InboxThreadView>(
        inboxThreadQueryKey(threadId),
      );
      const previousPages = queryClient.getQueriesData<InfiniteData<InboxThreadsPageView, unknown>>(
        {
          queryKey: ["inboxThreads"],
        },
      );

      queryClient.setQueryData<InboxThreadView>(inboxThreadQueryKey(threadId), (current) => {
        if (!current) return current;
        return applyThreadReadState(current, Math.max(current.unreadCount, 1));
      });

      queryClient.setQueriesData<InfiniteData<InboxThreadsPageView, unknown>>(
        { queryKey: ["inboxThreads"] },
        (current) =>
          updateInboxThreadInPages(current, threadId, (thread) => ({
            ...thread,
            hasUnread: true,
            unreadCount: Math.max(thread.unreadCount, 1),
          })),
      );

      return { previousThread, previousPages };
    },
    onError: (
      _error: unknown,
      _variables: void,
      context?: {
        previousThread?: InboxThreadView;
        previousPages?: Array<
          readonly [unknown, InfiniteData<InboxThreadsPageView, unknown> | undefined]
        >;
      },
    ) => {
      if (context?.previousThread) {
        queryClient.setQueryData(inboxThreadQueryKey(threadId), context.previousThread);
      }

      for (const [queryKey, data] of context?.previousPages || []) {
        queryClient.setQueryData(queryKey as readonly unknown[], data);
      }
    },
    onSettled: async () => {},
  };
}

export function deleteThreadMutationOptions(queryClient: QueryClient, threadId: string) {
  return {
    mutationFn: () => deleteThread({ data: { threadId } }),
    onMutate: async () => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: inboxThreadQueryKey(threadId) }),
        queryClient.cancelQueries({ queryKey: ["inboxThreads"] }),
      ]);

      const previousThread = queryClient.getQueryData<InboxThreadView>(
        inboxThreadQueryKey(threadId),
      );
      const previousPages = queryClient.getQueriesData<InfiniteData<InboxThreadsPageView, unknown>>(
        {
          queryKey: ["inboxThreads"],
        },
      );

      queryClient.setQueriesData<InfiniteData<InboxThreadsPageView, unknown>>(
        { queryKey: ["inboxThreads"] },
        (current) => removeInboxThreadFromPages(current, threadId),
      );

      return { previousThread, previousPages };
    },
    onError: (
      _error: unknown,
      _variables: void,
      context?: {
        previousThread?: InboxThreadView;
        previousPages?: Array<
          readonly [unknown, InfiniteData<InboxThreadsPageView, unknown> | undefined]
        >;
      },
    ) => {
      if (context?.previousThread) {
        queryClient.setQueryData(inboxThreadQueryKey(threadId), context.previousThread);
      }

      for (const [queryKey, data] of context?.previousPages || []) {
        queryClient.setQueryData(queryKey as readonly unknown[], data);
      }
    },
    onSettled: async () => {},
  };
}
